import { Button, Divider, Flex, Form, Input, Space, Typography } from 'antd';
import { FC } from 'react';
import { ArrowDown } from 'react-bootstrap-icons';

import { ButtonSuccess } from '@/common/components';
import { AppFormProps, BaseQueryParams } from '@/common/interfaces';
import { defineInputStatus } from '@/common/utils';

import {
  PageSearchAndReplaceFormFields,
  PageSearchAndReplaceFormInputs,
  useAutocompleteSearchAndReplaceQuery,
} from '@/modules/Pages';

import { PagesSearchAndReplaceFormAutocompleteLabel } from './PagesSearchAndReplaceFormAutocompleteLabel';
import { PagesSearchAndReplaceFormToggles } from './PagesSearchAndReplaceFormToggles';

interface Props extends AppFormProps<typeof PageSearchAndReplaceFormInputs, PageSearchAndReplaceFormFields> {
  autocompleteData: BaseQueryParams<typeof useAutocompleteSearchAndReplaceQuery>['data'] | undefined;
  autocompleteIsLoading: boolean;
  autocompleteIsFetching: boolean;
  autocompleteIsError: boolean;
  autocompleteError: BaseQueryParams<typeof useAutocompleteSearchAndReplaceQuery>['error'] | null;
}

export const PagesSearchAndReplaceFormView: FC<Props> = ({
  autocompleteData,
  autocompleteIsLoading,
  autocompleteIsFetching,
  autocompleteIsError,
  autocompleteError,
  formName,
  form,
  initialValues,
  formErrors,
  isFormLoading,
  validationSchema,
  handleBack,
  handleFinish,
  handleFinishFailed,
}) => {
  return (
    <Form
      form={form}
      name={formName}
      layout="vertical"
      initialValues={initialValues}
      disabled={isFormLoading}
      autoComplete="off"
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
    >
      <Form.Item
        label="Знайти"
        name={PageSearchAndReplaceFormInputs.SearchFor}
        hasFeedback
        help={formErrors?.searchFor}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.searchFor)}
        rules={validationSchema.searchFor}
      >
        <Input suffix={<PagesSearchAndReplaceFormToggles />} allowClear />
      </Form.Item>

      <Divider>
        <Typography.Text type="secondary">
          <Flex align="center" justify="center">
            <ArrowDown />
          </Flex>
        </Typography.Text>
      </Divider>

      <Form.Item
        label="Замінити на"
        name={PageSearchAndReplaceFormInputs.ReplaceWith}
        hasFeedback
        help={formErrors?.replaceWith}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.replaceWith)}
        rules={validationSchema.replaceWith}
      >
        <Input allowClear disabled={isFormLoading || autocompleteIsFetching || autocompleteIsLoading} />
      </Form.Item>

      <Form.Item wrapperCol={{ span: 24 }} style={{ marginBottom: 0 }}>
        <Flex justify="space-between" align="center" gap={8}>
          <PagesSearchAndReplaceFormAutocompleteLabel
            autocompleteData={autocompleteData}
            autocompleteIsLoading={autocompleteIsLoading}
            autocompleteIsFetching={autocompleteIsFetching}
            autocompleteIsError={autocompleteIsError}
            autocompleteError={autocompleteError}
          />

          <Space align="center" style={{ justifyContent: 'flex-end' }}>
            <Button
              type="default"
              onClick={handleBack}
              disabled={isFormLoading || autocompleteIsFetching || autocompleteIsLoading}
            >
              Скасувати
            </Button>
            <ButtonSuccess
              loading={isFormLoading}
              type="primary"
              htmlType="submit"
              disabled={isFormLoading || autocompleteIsFetching || autocompleteIsLoading}
            >
              Замінити
            </ButtonSuccess>
          </Space>
        </Flex>
      </Form.Item>
    </Form>
  );
};
